import type { ContraceptionState } from "./ContraceptionContext";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";

type PatchPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;

type ObGynContraception = NonNullable<
  NonNullable<PatchPayload["obGynHistory"]>["contraceptions"]
>[number];

type MaleContraception = NonNullable<
  NonNullable<PatchPayload["maleHistory"]>["contraceptions"]
>[number];

function buildMalePayload(state: ContraceptionState): PatchPayload {
  const maleContraceptionsPayload: MaleContraception[] = [];

  state.contraceptionIdsToDelete.forEach((id) => {
    if (id) {
      maleContraceptionsPayload.push({ id, method: "other", op: "delete" });
    }
  });

  if (state.usesContraception === true) {
    const deleteIdSet = new Set(state.contraceptionIdsToDelete);
    state.contraceptions
      .filter((c) => !c.id || !deleteIdSet.has(c.id))
      .forEach((c) => {
        const entry: MaleContraception = {
          method: c.method as MaleContraception["method"],
        };
        if (c.id) {
          entry.id = c.id;
        }
        if (c.other != null) {
          entry.other = c.other;
        }
        if (c.contraceptionName != null) {
          entry.contraceptionName = c.contraceptionName;
        }
        if (c.startDate != null) {
          entry.startDate = c.startDate;
        }
        if (c.endDate != null) {
          entry.endDate = c.endDate;
        }
        if (c.notes != null) {
          entry.notes = c.notes;
        }
        maleContraceptionsPayload.push(entry);
      });
  } else if (state.usesContraception === false) {
    const deleteIdSet = new Set(state.contraceptionIdsToDelete);
    state.contraceptions.forEach((c) => {
      if (c.id && !deleteIdSet.has(c.id)) {
        maleContraceptionsPayload.push({
          id: c.id,
          method: c.method as MaleContraception["method"],
          op: "delete",
        });
      }
    });
  }

  return {
    maleHistory: {
      ...(maleContraceptionsPayload.length > 0 && {
        contraceptions: maleContraceptionsPayload,
      }),
    },
    obGynHistory: {
      hasContraceptionHistory: state.usesContraception,
    },
  };
}

function buildAfabPayload(state: ContraceptionState): PatchPayload {
  const contraceptionsPayload: ObGynContraception[] = [];

  state.contraceptionIdsToDelete.forEach((id) => {
    if (id) {
      contraceptionsPayload.push({ id, method: "other", op: "delete" });
    }
  });

  if (state.usesContraception === true) {
    const deleteIdSet = new Set(state.contraceptionIdsToDelete);
    state.contraceptions
      .filter((c) => !c.id || !deleteIdSet.has(c.id))
      .forEach((c) => {
        const entry: ObGynContraception = {
          method: c.method as ObGynContraception["method"],
        };
        if (c.id) {
          entry.id = c.id;
        }
        if (c.other != null) {
          entry.other = c.other;
        }
        if (c.contraceptionName != null) {
          entry.contraceptionName = c.contraceptionName;
        }
        if (c.startDate != null) {
          entry.startDate = c.startDate;
        }
        if (c.endDate != null) {
          entry.endDate = c.endDate;
        }
        if (c.daysTillPeriodAfterStopping != null) {
          entry.daysTillPeriodAfterStopping = c.daysTillPeriodAfterStopping;
        }
        if (c.notes != null) {
          entry.notes = c.notes;
        }
        contraceptionsPayload.push(entry);
      });
  } else if (state.usesContraception === false) {
    const deleteIdSet = new Set(state.contraceptionIdsToDelete);
    state.contraceptions.forEach((c) => {
      if (c.id && !deleteIdSet.has(c.id)) {
        contraceptionsPayload.push({
          id: c.id,
          method: c.method as ObGynContraception["method"],
          op: "delete",
        });
      }
    });
  }

  return {
    obGynHistory: {
      hasContraceptionHistory: state.usesContraception,
      ...(contraceptionsPayload.length > 0 && {
        contraceptions: contraceptionsPayload,
      }),
    },
  };
}

export function transformContraceptionStateToRequest(
  state: ContraceptionState,
): PatchPayload {
  if (state.usesContraception === null) {
    return {};
  }

  if (state.userGender === "male") {
    return buildMalePayload(state);
  }

  return buildAfabPayload(state);
}
